import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeft, Upload } from "lucide-react";
import api from "@/lib/api";
import { slugify } from "@/lib/content";
import { COLLECTIONS } from "@/lib/collections";
import { ImageField } from "@/components/admin/ImageField";
import RichTextEditor from "@/components/admin/RichTextEditor";

function RepeatField({ field, value, onChange }) {
  const rows = Array.isArray(value) ? value : [];
  const update = (i, subName, v) => {
    const next = rows.map((r, ri) => (ri === i ? { ...r, [subName]: v } : r));
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {rows.map((row, i) => (
        <div key={i} className="border border-ash bg-white p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {field.subFields.map((sub) => (
              <label key={sub.name} className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slategray">{sub.label}</span>
                {sub.type === "textarea" ? (
                  <textarea rows={2} className="field resize-none" value={row[sub.name] || ""} onChange={(e) => update(i, sub.name, e.target.value)} />
                ) : (
                  <input className="field" value={row[sub.name] || ""} onChange={(e) => update(i, sub.name, e.target.value)} />
                )}
              </label>
            ))}
          </div>
          <button
            type="button"
            onClick={() => onChange(rows.filter((_, ri) => ri !== i))}
            className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-progress"
          >
            <Trash2 size={12} /> Remove row
          </button>
        </div>
      ))}
      <button
        type="button"
        data-testid={`add-${field.name}-row`}
        onClick={() => onChange([...rows, {}])}
        className="inline-flex items-center gap-2 border border-obsidian/20 px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] hover:border-progress hover:text-progress"
      >
        <Plus size={13} /> Add row
      </button>
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  switch (field.type) {
    case "textarea":
      return <textarea data-testid={`field-${field.name}`} rows={4} className="field resize-none" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    case "rich":
      return <RichTextEditor value={value || ""} onChange={onChange} placeholder={`Write the ${field.label.toLowerCase()} here…`} />;
    case "image":
      return <ImageField value={value} onChange={onChange} />;
    case "switch":
      return (
        <button
          type="button"
          data-testid={`field-${field.name}`}
          onClick={() => onChange(!value)}
          className={`flex h-7 w-14 items-center px-1 transition-colors ${value ? "bg-progress" : "bg-ash"}`}
          role="switch"
          aria-checked={Boolean(value)}
        >
          <span className={`block h-5 w-5 bg-white transition-transform ${value ? "translate-x-7" : "translate-x-0"}`} />
        </button>
      );
    case "select":
      return (
        <select data-testid={`field-${field.name}`} className="border border-obsidian/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-progress" value={value || ""} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {(field.options || []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    case "number":
      return <input data-testid={`field-${field.name}`} type="number" className="field" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} />;
    case "repeat":
      return <RepeatField field={field} value={value} onChange={onChange} />;
    default:
      return <input data-testid={`field-${field.name}`} className="field" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  }
}

export default function CollectionAdmin({ name }) {
  const cfg = COLLECTIONS[name];
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const load = () => {
    api.get(`/admin/${name}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  };
  useEffect(load, [name]);

  useEffect(() => {
    setEditing(null);
    setItems(null);
  }, [name]);

  const startNew = () => {
    const blank = {};
    cfg.fields.forEach((f) => {
      blank[f.name] = f.type === "repeat" ? [] : f.type === "switch" ? f.name === "published" : f.type === "number" ? 0 : "";
    });
    setForm(blank);
    setEditing("new");
  };

  const startEdit = (item) => {
    setForm({ ...item });
    setEditing(item.id);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!form.slug && form.title) form.slug = slugify(form.title);
      if (editing === "new") await api.post(`/admin/${name}`, form);
      else await api.put(`/admin/${name}/${editing}`, form);
      toast.success(`${cfg.label.replace(/s$/, "")} saved`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (confirmId !== id) {
      setConfirmId(id);
      setTimeout(() => setConfirmId(null), 3000);
      return;
    }
    try {
      await api.delete(`/admin/${name}/${id}`);
      toast.success("Deleted");
      setConfirmId(null);
      load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  if (editing !== null) {
    return (
      <div data-testid="admin-editor">
        <button data-testid="editor-cancel" onClick={() => setEditing(null)} className="mb-8 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-slategray hover:text-progress">
          <ArrowLeft size={14} /> Back to list
        </button>
        <form onSubmit={save}>
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-x-12">
            {cfg.fields.map((f) => (
              <label key={f.name} className={`flex flex-col gap-2 ${f.type === "rich" || f.type === "repeat" ? "lg:col-span-2" : ""}`}>
                <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slategray">
                  {f.label}
                  {f.required && <span className="text-progress">*</span>}
                  {f.hint && <span className="font-medium normal-case tracking-normal text-slategray/70">— {f.hint}</span>}
                </span>
                <FieldInput field={f} value={form[f.name]} onChange={(v) => setForm((prev) => ({ ...prev, [f.name]: v }))} />
              </label>
            ))}
          </div>
          <div className="sticky bottom-0 mt-10 border-t border-ash bg-paper py-4">
            <button
              type="submit"
              data-testid="editor-save"
              disabled={saving}
              className="bg-progress px-8 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-progress-dark disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid={`admin-collection-${name}`}>
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-4xl font-medium tracking-tight">{cfg.label}</h1>
        <button data-testid={`new-${name}-button`} onClick={startNew} className="inline-flex items-center gap-2 bg-obsidian px-5 py-3 text-[12px] font-bold uppercase tracking-[0.14em] text-paper transition-colors hover:bg-progress">
          <Plus size={15} /> New
        </button>
      </div>
      {items === null ? (
        <p className="mt-10 text-xs uppercase tracking-[0.25em] text-slategray">Loading…</p>
      ) : items.length === 0 ? (
        <div className="mt-10 border border-ash p-14 text-center">
          <p className="font-serif text-2xl">Nothing here yet.</p>
          <p className="mt-2 text-sm text-slategray">Create the first entry with the button above.</p>
        </div>
      ) : (
        <table className="mt-8 w-full border border-ash text-left text-sm">
          <thead>
            <tr className="border-b border-ash bg-ash/40 text-[11px] uppercase tracking-[0.16em] text-slategray">
              <th className="p-4 font-bold">Entry</th>
              <th className="hidden p-4 font-bold md:table-cell">Updated</th>
              <th className="p-4 font-bold">Status</th>
              <th className="p-4 font-bold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-ash/70 last:border-b-0" data-testid={`row-${item.id}`}>
                <td className="p-4 font-semibold">{item[cfg.titleField] || "(untitled)"}</td>
                <td className="hidden p-4 text-slategray md:table-cell">{(item.updated_at || "").slice(0, 10)}</td>
                <td className="p-4">
                  <span className={`inline-block h-2 w-2 rounded-full ${item.published ? "bg-green-600" : "bg-slategray"}`} aria-hidden="true" />
                  <span className="ml-2 text-[12px]">{item.published ? "Published" : "Draft"}</span>
                </td>
                <td className="p-4 text-right">
                  <button data-testid={`edit-${item.id}`} onClick={() => startEdit(item)} className="mr-3 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.1em] hover:text-progress">
                    <Pencil size={13} /> Edit
                  </button>
                  <button data-testid={`delete-${item.id}`} onClick={() => remove(item.id)} className={`inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.1em] ${confirmId === item.id ? "text-progress" : "text-slategray hover:text-progress"}`}>
                    <Trash2 size={13} /> {confirmId === item.id ? "Confirm?" : "Delete"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
